import React from "react";
import { Avatar } from "@mui/material";
import { format, isSameDay, isSameYear, subDays } from "date-fns";

export const OUTLOOK_FONT =
  '"Segoe UI", "Segoe UI Web (West European)", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", sans-serif';

export const OUTLOOK = {
  blue: "#0f6cbd",
  blueDark: "#115ea3",
  paneBg: "#f0f0f0",
  listBg: "#ffffff",
  border: "#e1dfdd",
  hover: "#f5f5f5",
  selected: "#cfe4fa",
  selectedHover: "#c1dcf7",
  checked: "#e8f1fb",
  text: "#242424",
  textSecondary: "#616161",
  textMuted: "#707070",
  folderSelected: "#e0e0e0",
};

/** Outlook persona colors for sender avatars. */
const AVATAR_COLORS = [
  "#0078d4",
  "#8764b8",
  "#038387",
  "#ca5010",
  "#498205",
  "#c239b3",
  "#986f0b",
  "#004e8c",
  "#da3b01",
  "#00b7c3",
  "#5c2e91",
  "#107c10",
];

export function senderInitials(name?: string | null, address?: string | null): string {
  const source = (name || address || "?").replace(/[[(<].*$/, "").trim();
  if (source.includes(",")) {
    const [last, first] = source.split(",").map((s) => s.trim());
    return `${first?.[0] ?? ""}${last?.[0] ?? ""}`.toUpperCase() || "?";
  }
  const parts = source.split(/[\s@._-]+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function avatarColor(key: string): string {
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash * 31 + key.charCodeAt(i)) | 0;
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

export const SenderAvatar: React.FC<{
  name?: string | null;
  address?: string | null;
  size?: number;
}> = ({ name, address, size = 32 }) => (
  <Avatar
    sx={{
      width: size,
      height: size,
      fontSize: size * 0.4,
      fontWeight: 600,
      fontFamily: OUTLOOK_FONT,
      bgcolor: avatarColor((address || name || "").toLowerCase()),
    }}
  >
    {senderInitials(name, address)}
  </Avatar>
);

/** Outlook list timestamp: time today, weekday + time this week, else date. */
export function formatListTime(value: Date | string): string {
  const date = new Date(value);
  const now = new Date();
  if (isSameDay(date, now)) return format(date, "h:mm a");
  if (date > subDays(now, 6)) return format(date, "EEE h:mm a");
  if (isSameYear(date, now)) return format(date, "EEE M/d");
  return format(date, "M/d/yyyy");
}

/** Outlook reading-pane timestamp, e.g. "Tue 10/6/2026 2:32 PM". */
export function formatReadingTime(value: Date | string): string {
  return format(new Date(value), "EEE M/d/yyyy h:mm a");
}

/** Sender display name without bracketed org suffixes. */
export function senderDisplayName(name?: string | null, address?: string | null): string {
  return (name || address || "Unknown sender").trim();
}
